import { DEFAULT_LOCALE, type LocaleCode } from '~/constants/i18n';
import { localizePath } from '~/utils/i18n/locale';

// Production origin; NUXT_PUBLIC_SITE_URL (runtimeConfig.public.appUrl) overrides it per environment.
const SITE_BASE_DEFAULT = 'https://insidersofdubai.com';
const X_DEFAULT_HREFLANG = 'x-default';

export type HreflangLink = { hreflang: string; href: string };

export type LocalizedHeadLinks = { canonical: string; alternates: HreflangLink[] };

/** Absolute site origin without a trailing slash — same source + fallback as the sitemaps. */
export const resolveSiteBase = (raw: string | null | undefined): string => {
  const cleaned = (raw ?? '').trim().replace(/\/+$/, '');
  return cleaned || SITE_BASE_DEFAULT;
};

/** Absolute URL of an English (unprefixed) path in `locale`. */
export const localizedAbsoluteUrl = (base: string, englishPath: string, locale: LocaleCode): string =>
  `${base}${localizePath(englishPath, DEFAULT_LOCALE, locale)}`;

/**
 * `<link rel="alternate" hreflang>` targets for an English (unprefixed, query-free) path:
 * `x-default` → the English URL, then one per enabled locale. Empty when only one locale is
 * enabled (nothing to alternate between).
 */
export const buildHreflangLinks = ({
  base,
  path,
  enabled,
}: {
  base: string;
  path: string;
  enabled: readonly LocaleCode[];
}): HreflangLink[] => {
  if (enabled.length < 2) return [];
  return [
    { hreflang: X_DEFAULT_HREFLANG, href: localizedAbsoluteUrl(base, path, DEFAULT_LOCALE) },
    ...enabled.map((code) => ({ hreflang: code, href: localizedAbsoluteUrl(base, path, code) })),
  ];
};

/**
 * Canonical (current locale's URL) + hreflang alternates for a localized route's path. When the
 * page declares a different canonical (`canonicalPath`, English/unprefixed — e.g.
 * `/properties/<id>` canonicalising to `/projects/<dev>/<slug>`), the current URL is not the
 * indexable one: canonical points there and no alternates are emitted for this URL.
 */
export const buildLocalizedHeadLinks = ({
  base,
  path,
  locale,
  enabled,
  canonicalPath,
}: {
  base: string;
  /** Current route path, possibly `/<locale>`-prefixed; query and hash are ignored. */
  path: string;
  locale: LocaleCode;
  enabled: readonly LocaleCode[];
  /** Page-declared canonical (English, unprefixed path), if any. */
  canonicalPath?: string | null;
}): LocalizedHeadLinks => {
  const pathname = path.split(/[?#]/)[0] || '/';
  const englishPath = localizePath(pathname, locale, DEFAULT_LOCALE);
  if (canonicalPath && canonicalPath !== englishPath) {
    return { canonical: localizedAbsoluteUrl(base, canonicalPath, locale), alternates: [] };
  }
  return {
    canonical: localizedAbsoluteUrl(base, englishPath, locale),
    alternates: buildHreflangLinks({ base, path: englishPath, enabled }),
  };
};

// A dotted message key (namespace.section.key) such as `property.seo.titleWithDeveloper` — what vue-i18n renders when the
// key's messages have not loaded. Search engines index whatever the page shows at crawl time.
const RAW_MESSAGE_KEY = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9_-]+){2,}$/;

/** True when `text` is an untranslated message key rather than real copy. */
export const isRawMessageKey = (text: string | null | undefined): boolean => RAW_MESSAGE_KEY.test((text ?? '').trim());

/**
 * SEO copy from a translation, or `fallback` when the translation is empty or came back as its own
 * key. Titles and descriptions must never publish a raw key.
 */
export const seoText = (translated: string | null | undefined, fallback: string): string =>
  !translated?.trim() || isRawMessageKey(translated) ? fallback : translated;
